import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as conversationHelpers from '#providers/conversations/Conversations.helpers.ts';
import { useConversationState } from '#providers/conversations/use-conversation-state/index.ts';
import type { TConversationThreadContextValue } from '#providers/conversations/index.ts';
import { useExperience } from '#providers/experiences/use-experience/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import {
  ExperienceChatContext,
  ExperienceChatThreadContext,
} from './ExperienceChat.context.ts';
import * as helpers from './ExperienceChat.helpers.ts';
import type {
  TAnswerStream,
  TExperienceAskPayload,
  TExperienceAskRequest,
  TExperienceChatContextValue,
  TExperienceChatProviderProps,
  TFollowUps,
} from './ExperienceChat.types.ts';

const ASK_PATH = '/ask_guided_experience_v2_sse';
const askMutationKey = ['experiences', 'ask'];

export const ExperienceChatProvider = ({
  sse,
  userClient,
  userAgent,
  children,
}: TExperienceChatProviderProps) => {
  const { requireProjectId } = useProjects();
  const {
    experienceId,
    experience,
    currentStepId,
    saveAnswers,
    recordMessages,
  } = useExperience();

  const {
    state: { messages: requestMessages },
    dispatchUpdate,
    hasPendingAnswer,
    sendRequest,
    removeRequest,
    failAnswer,
  } = useConversationState();

  const [followUps, setFollowUps] = useState<TFollowUps>();
  const closerRef = useRef<AbortController>(undefined);

  const storedMessages = experience?.messages;
  const messages = useMemo(() => {
    const stored = helpers.toStoredMessages(storedMessages, requestMessages);

    return [...stored, ...requestMessages];
  }, [storedMessages, requestMessages]);

  const followUpQuestions = useMemo(
    () =>
      followUps && followUps.stepId === currentStepId
        ? followUps.questions
        : [],
    [followUps, currentStepId],
  );

  const { mutateAsync: readAnswer } = useMutation({
    mutationKey: askMutationKey,
    mutationFn: async ({
      requestId,
      request,
      signal,
      stepId,
    }: TAnswerStream) => {
      const stream = await sse.open(ASK_PATH, {
        method: 'POST',
        body: request,
        reconnect: false,
        signal,
      });

      let answerFollowUpQuestions: string[] = [];

      for await (const event of stream) {
        const action = conversationHelpers.toServerAction(requestId, event);

        if (!action) {
          continue;
        }

        if (action.type === 'chat.follow_up_questions') {
          answerFollowUpQuestions = action.envelope.payload.questions;
        }

        dispatchUpdate(action);

        if (action.type !== 'chat.answer.done') {
          continue;
        }

        const answered = helpers.toMessageInfos(
          request.question,
          action.envelope.payload,
          requestId,
        );

        recordMessages(answered);
        setFollowUps({ stepId, questions: answerFollowUpQuestions });

        return;
      }

      failAnswer(requestId, helpers.interruptedAnswer);
    },
  });

  const streamAnswer = useCallback(
    async (
      requestId: string,
      request: TExperienceAskRequest,
      stepId?: string,
    ) => {
      const closer = new AbortController();

      closerRef.current = closer;
      await saveAnswers();
      await readAnswer({ requestId, request, signal: closer.signal, stepId });
    },
    [saveAnswers, readAnswer],
  );

  const ask = useCallback(
    async ({ question, force }: TExperienceAskPayload) => {
      const request: TExperienceAskRequest = {
        project_id: requireProjectId(),
        guided_experience_id: experienceId,
        question,
        force,
        user_client: userClient,
        user_agent: userAgent,
      };

      const requestPayload = {
        project_id: request.project_id,
        question,
        force,
      };

      const sendQuestion = async (requestId: string) => {
        setFollowUps(undefined);
        await streamAnswer(requestId, request, currentStepId);
      };

      await sendRequest(requestPayload, sendQuestion);
    },
    [
      experienceId,
      currentStepId,
      userClient,
      userAgent,
      requireProjectId,
      sendRequest,
      streamAnswer,
    ],
  );

  useEffect(() => () => closerRef.current?.abort(), []);

  const value = useMemo<TExperienceChatContextValue>(
    () => ({ hasPendingAnswer, ask, removeRequest }),
    [hasPendingAnswer, ask, removeRequest],
  );

  const threadValue = useMemo<TConversationThreadContextValue>(
    () => ({ messages, followUpQuestions }),
    [messages, followUpQuestions],
  );

  return (
    <ExperienceChatContext.Provider value={value}>
      <ExperienceChatThreadContext.Provider value={threadValue}>
        {children}
      </ExperienceChatThreadContext.Provider>
    </ExperienceChatContext.Provider>
  );
};
